/**
 * Ticking a milestone off, once, for both screens that offer it.
 *
 * The same checkbox behaved differently depending on which tab it was drawn on.
 * The list awaited the server before anything moved and said `common.error` when
 * the write failed; the board flipped the circle immediately and, on failure,
 * silently reset its whole array back to the `project.milestones` prop - no
 * message, no explanation, and any other row the operator had touched since the
 * last reload went back with it. Two behaviours for one control means the
 * operator has to learn which tab they are on before they can trust a tick.
 *
 * The behaviour kept here is OPTIMISTIC PLUS A VISIBLE ERROR, for two reasons.
 * Optimistic wins over await-then-draw because a milestone tick is a one-bit
 * change that is almost always accepted, and a checkbox that hangs for a round
 * trip reads as a checkbox that did not register the click - which is how you
 * get the operator clicking it twice. And the silent rollback goes because a
 * change that undoes itself with no word is indistinguishable from one that was
 * never made: the next thing that happens is the operator ticking it again.
 *
 * The rollback is also narrower than the board's was. Only the row that was
 * clicked goes back, to exactly the value it had at the click, so a failed write
 * on one milestone cannot discard a successful one on another.
 */
import { useCallback, type Dispatch, type SetStateAction } from "react";
import { useT } from "../../../i18n/I18nProvider";
import { updateMilestone, type Milestone } from "../../../lib/projects";
import { useToast } from "../../ui";

/**
 * `setItems` is the caller's own list state, so the hook can move one row
 * without owning the list: both screens keep their milestones in sync with a
 * prop from the project record and would fight a second copy held in here.
 *
 * `onChange` is the caller's "the server and I now disagree, refetch" signal -
 * fired only after the write actually lands, because a milestone tick shifts
 * counters on the project header that this hook cannot see.
 */
export function useMilestoneToggle(
  setItems: Dispatch<SetStateAction<Milestone[]>>,
  onChange: () => void,
): (m: Milestone) => void {
  const t = useT();
  const toast = useToast();

  return useCallback(
    (m: Milestone) => {
      const done = !m.done;
      const patchRow = (next: Partial<Milestone>) =>
        setItems((prev) => prev.map((x) => (x.id === m.id ? { ...x, ...next } : x)));

      patchRow({ done });
      void updateMilestone(m.id, { done })
        .then((saved) => {
          // The server's copy, not the guess: it is the only one that carries
          // whatever else the update touched.
          patchRow(saved);
          onChange();
        })
        .catch(() => {
          patchRow({ done: m.done });
          toast(t("common.error"));
        });
    },
    [setItems, onChange, toast, t],
  );
}
